// helpful SO links
// https://stackoverflow.com/questions/49390912/why-would-stripe-paymentrequest-canmakepayment-fail-on-localhost
// https://stackoverflow.com/questions/60865022/stripe-payment-request-button-with-subscriptions
import React, { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { Divider, Flex, Text } from 'theme-ui'
import { Button } from 'ui'
import { PaymentMethod, submitCreditCardPayment, TransactionStatus } from 'store/billing'
import ContinueToCoingateButton from './Coingate/ContinueToCoingateButton'
import XPay from 'components/Plans/Payments/XPay/XPay'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { navigate } from 'gatsby'
import { RenderProcessing } from './PaymentForm'
import useStripeElements from './Stripe/useStripeElements'
import {
  TrackingEventAction,
  TrackingEventCategory,
  useTrackingHelper,
} from 'utils/useTrackingHelper'
import { CAN_I_HAZ_TRIAL } from 'store/api/user/user.interface'
import PromoCodeContainer from 'components/Plans/SelectPlan/PromoCode/PromoCodeContainer'
import getPriceDetailsForCurrency from 'utils/getPriceDetailsForCurrency'
import getFormattedPrice from 'utils/getFormattedPrice'
import { useActivateTrialOrFreePromoCodeMutation } from 'store/api/billing/payments'
import { useGetProductsQuery } from 'store/api/billing/products'
import { CommonPriceInterval, ProductType } from 'store/api/billing/payments.interface'
import { useGetDevicesQuery } from 'store/api/devices'
import { userApi } from 'store/api/user'
import useGetUser from 'components/Dashboard/utils/useGetUser'

const getTheAmountOfDiscountPercent = (oldPrice, price) => ((oldPrice - price) / oldPrice) * 100

const Summary = ({
  fullName,
  is100PercentPromoCodeApplied,
  isAlreadyBilled,
  areCreditCardInputsValid,
  onNextClick,
}: {
  fullName: string
  is100PercentPromoCodeApplied: boolean
  isAlreadyBilled: boolean
  areCreditCardInputsValid: boolean
  onNextClick: () => void
}): ReactElement => {
  const dispatch = useAppDispatch()
  const { trackEvent } = useTrackingHelper()
  const { transactionStatus, paymentRequest, paymentMethod } = useAppSelector(s => s.payments)
  const sessionToken = useAppSelector(s => s.persistData.sessionToken)
  const { data: devicesData } = useGetDevicesQuery('')
  const isAnyDeviceConfigured = devicesData?.devices.some(d => d.status)
  const { data: userData } = useGetUser()
  const email = userData?.email || ''
  const [activateTrialOrFreePromoCode] = useActivateTrialOrFreePromoCodeMutation()

  const {
    priceInterval,
    selectedProductPk,
    currentProduct,
    selectedCurrency,
    promoMode,
    promoCode,
  } = useAppSelector(s => s.products)
  const { data: productsData } = useGetProductsQuery({ promoMode, promoCode })

  const selectedProduct = productsData?.products?.find(p => p.PK === selectedProductPk)
  const selectedPricePoint = selectedProduct?.price_points.find(pp => pp.duration === priceInterval)
  const selectedPrice =
    selectedPricePoint && getPriceDetailsForCurrency(selectedPricePoint, selectedCurrency?.code)
  const shouldForceTrial = useAppSelector(s => s.tracking.shouldForceTrial)
  const isFullNameComplete = fullName.length !== 0
  const amountOfDiscount = isAlreadyBilled
    ? 100
    : selectedPricePoint?.original_price &&
      getTheAmountOfDiscountPercent(selectedPricePoint.original_price, selectedPricePoint.price)
  const priceDuration = selectedPricePoint?.duration || 0
  const areSeveralMonths =
    priceDuration > CommonPriceInterval.MONTHLY && priceDuration !== CommonPriceInterval.YEARLY

  useEffect(() => {
    // need to have user email after refresh
    dispatch(userApi.endpoints.getUser.initiate(''))
  }, [dispatch])

  const { stripe, cardElement } = useStripeElements()

  const creditCardSubmitEnabled = areCreditCardInputsValid && isFullNameComplete
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (creditCardSubmitEnabled && sessionToken && stripe && cardElement) {
      await dispatch(submitCreditCardPayment({ stripe, cardElement, fullName, email }))
    }

    if (
      priceInterval === CommonPriceInterval.TRIAL ||
      is100PercentPromoCodeApplied ||
      isAlreadyBilled
    ) {
      if (priceInterval === CommonPriceInterval.TRIAL) {
        trackEvent({
          category: TrackingEventCategory.Payments,
          action: TrackingEventAction.Trial,
        })

        // request trial
        activateTrialOrFreePromoCode({ can_i_haz_trial: CAN_I_HAZ_TRIAL.YES })
      } else {
        // request 100% off promo code
        activateTrialOrFreePromoCode({ promo_code: promoCode })
      }

      return navigate(`/dashboard${isAnyDeviceConfigured ? '/account' : '/profiles'}`, {
        state: { login: true },
      })
    }
  }, [
    creditCardSubmitEnabled,
    sessionToken,
    stripe,
    cardElement,
    priceInterval,
    is100PercentPromoCodeApplied,
    isAlreadyBilled,
    dispatch,
    fullName,
    email,
    isAnyDeviceConfigured,
    trackEvent,
    activateTrialOrFreePromoCode,
    promoCode,
  ])

  useEffect(() => {
    if (
      priceInterval !== CommonPriceInterval.TRIAL &&
      transactionStatus === TransactionStatus.SUCCESS
    ) {
      onNextClick()
    }
  }, [transactionStatus, priceInterval, onNextClick])

  const isStripePayment =
    paymentMethod === PaymentMethod.GOOGLE ||
    paymentMethod === PaymentMethod.APPLE ||
    paymentMethod === PaymentMethod.BROWSER

  const onEditPlanClick = (): void => {
    navigate('/plans')
  }

  const isFormButtonDisabled = !(
    creditCardSubmitEnabled ||
    priceInterval === CommonPriceInterval.TRIAL ||
    is100PercentPromoCodeApplied ||
    isAlreadyBilled
  )
  const continueButtonAriaLabel = useMemo(() => {
    const isTrial = priceInterval == CommonPriceInterval.TRIAL || shouldForceTrial
    const productName =
      shouldForceTrial || selectedProduct?.name === ProductType.FULL
        ? ProductType.FULL
        : ProductType.SOME
    if (selectedProduct?.name === currentProduct?.name) {
      return `Confirm change to ${productName}`
    }
    if (isTrial) {
      return `Start ${productName} trial`
    }
    if (is100PercentPromoCodeApplied || isAlreadyBilled) {
      return `Continue with promo code`
    }
    return `Purchase ${productName}`
  }, [
    currentProduct?.name,
    is100PercentPromoCodeApplied,
    isAlreadyBilled,
    priceInterval,
    selectedProduct?.name,
    shouldForceTrial,
  ])

  const shouldShowPromoCodeForm =
    !currentProduct?.subscription?.status &&
    !shouldForceTrial &&
    priceInterval !== CommonPriceInterval.TRIAL

  const price =
    priceInterval === CommonPriceInterval.TRIAL || isAlreadyBilled ? 0 : selectedPrice?.price

  const totalPrice = getFormattedPrice(selectedCurrency, price)

  return (
    <Flex
      sx={{
        width: ['100%', '34.8rem', '46.8rem'],
        flexDirection: 'column',
        mb: ['10rem', '2.4rem'],
      }}
    >
      <Flex
        sx={{
          width: '100%',
          p: '2.4rem',
          flexDirection: 'column',
          backgroundColor: 'smokyBlack80',
          borderRadius: '1.2rem',
          border: '1px solid',
          borderColor: 'blueYonder50',
          textAlign: 'left',
          height: 'fit-content',
          gap: '2.4rem',
        }}
      >
        <Flex sx={{ flexDirection: 'column', height: 'fit-content', gap: '2.4rem' }} tabIndex={0}>
          <Text
            variant="size21Weight600Line138"
            sx={{
              color: 'aliceBlue',
            }}
          >
            Summary
          </Text>
          <Text
            variant="size32Weight700Line138"
            sx={{
              color: selectedProduct?.name === ProductType.FULL ? 'berryBlue' : 'banana',
            }}
          >
            {priceInterval === CommonPriceInterval.TRIAL ? 'Some Control' : selectedProduct?.name}
          </Text>
          {priceInterval !== CommonPriceInterval.TRIAL ? (
            <>
              {!!amountOfDiscount && (
                <Flex sx={{ justifyContent: 'space-between', mb: ['1.6rem', '1.1rem'] }}>
                  <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
                    Promo Applied
                  </Text>
                  <Text
                    variant="size15Weight400"
                    sx={{ color: 'aliceBlue70' }}
                  >{`${amountOfDiscount}% Off`}</Text>
                </Flex>
              )}
              <Flex sx={{ justifyContent: 'space-between' }}>
                <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
                  {areSeveralMonths
                    ? priceDuration % CommonPriceInterval.YEARLY === 0
                      ? `${priceDuration / CommonPriceInterval.YEARLY} Years`
                      : `${priceDuration} Months`
                    : priceInterval === CommonPriceInterval.YEARLY
                    ? 'Yearly'
                    : 'Monthly'}{' '}
                  Plan
                  {priceDuration === CommonPriceInterval.YEARLY &&
                    !isAlreadyBilled &&
                    ' (Billed Annually)'}
                  {priceDuration === CommonPriceInterval.MONTHLY &&
                    !isAlreadyBilled &&
                    ' (Billed Monthly)'}
                </Text>
                <Text
                  variant="size15Weight400"
                  sx={{
                    color: 'aliceBlue70',
                    ml: ['1rem'],
                    flexShrink: 0,
                  }}
                >
                  {getFormattedPrice(selectedCurrency, isAlreadyBilled ? 0 : selectedPrice?.price)}
                </Text>
              </Flex>
            </>
          ) : (
            <Flex sx={{ justifyContent: 'space-between' }}>
              <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
                30 Day Trial
              </Text>
              <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
                Free
              </Text>
            </Flex>
          )}
          <Divider sx={{ m: 0, color: 'blueYonder50' }} />
          <Flex
            sx={{
              justifyContent: 'space-between',
            }}
            data-testid="summary-total"
          >
            <Text variant="size21Weight600Line138" sx={{ color: 'aliceBlue' }}>
              Total
            </Text>
            <Text variant="size21Weight400Line138" sx={{ color: 'aliceBlue' }}>
              {totalPrice}
            </Text>
          </Flex>
        </Flex>
        {transactionStatus === TransactionStatus.INPROGRESS && <RenderProcessing />}
        {transactionStatus !== TransactionStatus.INPROGRESS &&
          (paymentMethod === PaymentMethod.CRYPTO ? (
            <ContinueToCoingateButton pricePK={selectedPricePoint?.PK || ''} />
          ) : isStripePayment ? (
            paymentRequest && <XPay paymentMethod={paymentMethod} />
          ) : (
            <Button
              disabled={isFormButtonDisabled}
              variant="gradientButton"
              sx={{
                minWidth: '100%',
                height: '4.7rem',
                borderRadius: '100px',
              }}
              onClick={handleSubmit}
              data-testid="checkout-submit-button"
              tabIndex={isFormButtonDisabled ? -1 : 0}
              ariaLabel={continueButtonAriaLabel}
            >
              <Text variant="size17Weight500Line138">
                {`Finish${price ? ` and Pay ${totalPrice}` : ''}`}
              </Text>
            </Button>
          ))}
      </Flex>
      <Flex sx={{ width: '100%', justifyContent: 'center', mt: '3.2rem' }}>
        <Button
          variant="simple"
          ariaLabel="edit plan"
          onClick={onEditPlanClick}
          sx={{
            color: 'cyan800',
            p: 0,
          }}
        >
          <Text variant="size17Weight500Line138">Edit Plan</Text>
        </Button>
      </Flex>
      {shouldShowPromoCodeForm && (
        <Flex sx={{ width: '100%', justifyContent: 'center', alignItems: 'center', mt: '3.2rem' }}>
          <PromoCodeContainer tabsContainerSx={{ ml: 0 }} inputSx={{ ml: 0 }} />
        </Flex>
      )}
    </Flex>
  )
}

export default Summary
